import React, { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Rocket } from "lucide-react";

const DONE_KEY = "cm_demo_tour_done";
const ONBOARD_DONE_KEY = "cm_tour_done";
const START_FLAG = "cm_start_tour";
const CARD_W = 340;

const NAV_STEPS = [
  {
    target: "nav-schedule-desktop",
    title: "📅 Agenda",
    body: "Veja os serviços por dia, semana e mês, acompanhe cada equipe e reagende com poucos cliques.",
  },
  {
    target: "nav-clients-desktop",
    title: "👤 Clientes",
    body: "Cadastro completo com endereço no mapa, frequência e valor de cada casa atendida.",
  },
  {
    target: "nav-collaborators-desktop",
    title: "🧹 Colaboradores & Banco de Helps",
    body: "Gerencie sua equipe, veja no mapa e contrate ajudantes livres do banco quando precisar de reforço.",
  },
  {
    target: "nav-finance-desktop",
    title: "💰 Financeiro",
    body: "Pagamentos, recebimentos, faturas e relatórios financeiros — tudo num lugar só.",
  },
];

function buildSteps(mode) {
  if (mode === "demo") {
    return [
      {
        target: null,
        title: "👋 Bem-vindo à demonstração!",
        body: "Você está explorando o CleanManager com dados reais de exemplo — em modo somente leitura. Vou te mostrar as áreas principais em 5 passos rápidos.",
      },
      ...NAV_STEPS,
      {
        target: "demo-signup",
        title: "🚀 Gostou?",
        body: "Crie sua conta grátis e comece com 14 dias de teste. É rápido e sem cartão de crédito.",
        cta: { href: "/signup", label: "Criar conta grátis" },
      },
    ];
  }
  // onboarding (novo dono de empresa)
  return [
    {
      target: null,
      title: "🎉 Bem-vindo ao CleanManager!",
      body: "Sua empresa foi criada e você tem 14 dias de teste grátis. Vou te mostrar rapidinho onde fica cada coisa.",
    },
    ...NAV_STEPS,
    {
      target: null,
      title: "✅ Tudo pronto!",
      body: "Comece cadastrando seu primeiro cliente e montando a agenda. Bom trabalho! 💪",
      cta: { href: "/clients", label: "Cadastrar meu primeiro cliente" },
    },
  ];
}

export function DemoTour({ active, onboarding }) {
  const mode = active ? "demo" : "onboarding";
  const doneKey = active ? DONE_KEY : ONBOARD_DONE_KEY;
  const STEPS = React.useMemo(() => buildSteps(mode), [mode]);
  const [step, setStep] = useState(-1);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (active) {
      if (localStorage.getItem(DONE_KEY)) return;
      const t = setTimeout(() => setStep(0), 900);
      return () => clearTimeout(t);
    }
    if (onboarding) {
      if (localStorage.getItem(ONBOARD_DONE_KEY)) return;
      if (localStorage.getItem(START_FLAG) !== "1") return;
      const t = setTimeout(() => {
        localStorage.removeItem(START_FLAG);
        setStep(0);
      }, 1100);
      return () => clearTimeout(t);
    }
  }, [active, onboarding]);

  useEffect(() => {
    const restart = () => setStep(0);
    window.addEventListener("cm:demo-tour", restart);
    return () => window.removeEventListener("cm:demo-tour", restart);
  }, []);

  const cur = step >= 0 ? STEPS[step] : null;

  const measure = useCallback(() => {
    if (!cur) return;
    if (!cur.target) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${cur.target}"]`);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setRect(el.getBoundingClientRect());
  }, [cur]);

  useLayoutEffect(() => {
    if (step < 0) return;
    measure();
    const r = () => measure();
    window.addEventListener("resize", r);
    const t = setTimeout(measure, 350); // remeasure após o scroll
    return () => { window.removeEventListener("resize", r); clearTimeout(t); };
  }, [step, measure]);

  const finish = () => { localStorage.setItem(doneKey, "1"); setStep(-1); };

  if (step < 0 || !cur) return null;

  // Posição do card
  let cardStyle;
  if (rect) {
    const left = Math.min(rect.right + 16, window.innerWidth - CARD_W - 16);
    const top = Math.min(Math.max(rect.top - 8, 16), window.innerHeight - 240);
    cardStyle = { left: Math.max(left, 16), top };
  } else {
    cardStyle = { left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
  }

  const spotlight = rect
    ? {
        left: rect.left - 6, top: rect.top - 6,
        width: rect.width + 12, height: rect.height + 12,
      }
    : null;

  return (
    <div className="fixed inset-0 z-[9999]" style={{ pointerEvents: "none" }} data-testid="demo-tour">
      {/* Click-catcher / dim */}
      <div
        className="absolute inset-0"
        style={{
          pointerEvents: "auto",
          background: spotlight ? "transparent" : "rgba(15,23,42,0.62)",
        }}
      />
      {/* Spotlight */}
      {spotlight && (
        <div
          className="absolute rounded-xl transition-all duration-300"
          style={{
            ...spotlight,
            pointerEvents: "none",
            boxShadow: "0 0 0 9999px rgba(15,23,42,0.62)",
            outline: "3px solid #3b82f6",
          }}
        />
      )}
      {/* Card */}
      <div
        data-testid="demo-tour-card"
        className="absolute w-[340px] max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-2xl p-5 transition-all duration-300"
        style={{ ...cardStyle, pointerEvents: "auto" }}
      >
        <button onClick={finish} data-testid="demo-tour-skip"
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        <div className="text-xs font-semibold text-blue-600 mb-1">Passo {step + 1} de {STEPS.length}</div>
        <h3 className="text-lg font-bold text-slate-900">{cur.title}</h3>
        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{cur.body}</p>

        {/* Dots */}
        <div className="flex items-center gap-1.5 mt-4">
          {STEPS.map((s, i) => (
            <span key={s.title} className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-blue-600" : "w-1.5 bg-slate-200"}`} />
          ))}
        </div>

        <div className="flex items-center justify-between mt-4">
          <button onClick={finish} data-testid="demo-tour-skip-2"
            className="text-sm text-slate-400 hover:text-slate-600">Pular</button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep((s) => s - 1)} data-testid="demo-tour-back"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border text-slate-600 text-sm font-semibold hover:bg-slate-50">
                <ArrowLeft size={15} /> Voltar
              </button>
            )}
            {cur.cta ? (
              <a href={cur.cta.href} onClick={finish} data-testid="demo-tour-cta"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                <Rocket size={15} /> {cur.cta.label}
              </a>
            ) : step === STEPS.length - 1 ? (
              <button onClick={finish} data-testid="demo-tour-finish"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                Concluir
              </button>
            ) : (
              <button onClick={() => setStep((s) => s + 1)} data-testid="demo-tour-next"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                Próximo <ArrowRight size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
